export interface RoutingContext {
  weight_goal?: string
  height_feet?: number
  height_inches?: number
  weight_lbs?: number
  glp1_experience?: string
  safety_screening?: Record<string, boolean>
  has_comorbidities?: boolean
  weight_related_conditions?: string[]
}

export interface RoutingResult {
  track: 'core' | 'microdose'
  reason: string
  show_bmi_redirect: boolean
  show_first_timer_education: boolean
  can_proceed: boolean
  requires_review: boolean
  bmi?: number
}

export function calculateBMI(heightFeet: number, heightInches: number, weightLbs: number): number {
  const totalInches = heightFeet * 12 + heightInches
  if (totalInches <= 0) return 0
  return (weightLbs / (totalInches * totalInches)) * 703
}

function getInitialTrack(weightGoal?: string): 'core' | 'microdose' {
  if (!weightGoal) return 'microdose'
  if (weightGoal === 'significant_loss') return 'core'
  return 'microdose'
}

function hasDisqualifyingConditions(safetyScreening?: Record<string, boolean>): boolean {
  if (!safetyScreening) return false
  const disqualifying = ['pregnant_planning', 'breastfeeding', 'mtc_men2']
  return disqualifying.some((key) => safetyScreening[key] === true)
}

function requiresProviderReview(safetyScreening?: Record<string, boolean>): boolean {
  if (!safetyScreening) return false
  return (
    safetyScreening.pancreatitis === true ||
    safetyScreening.gastroparesis === true ||
    safetyScreening.bowel_obstruction === true ||
    safetyScreening.eating_disorder === true
  )
}

const BMI_REVIEW_MIN = 20
const BMI_MICRO_MAX = 27
const BMI_COMORB_MIN = 27
const BMI_CORE_MIN = 30

export function determineTrack(context: RoutingContext): RoutingResult {
  const {
    weight_goal,
    height_feet = 0,
    height_inches = 0,
    weight_lbs = 0,
    glp1_experience,
    safety_screening,
    has_comorbidities,
    weight_related_conditions,
  } = context

  const bmi = height_feet && weight_lbs ? calculateBMI(height_feet, height_inches, weight_lbs) : undefined
  const inferredHasComorbidities =
    typeof has_comorbidities === 'boolean' ? has_comorbidities : (weight_related_conditions || []).length > 0

  let finalTrack: 'core' | 'microdose'
  let showBmiRedirect = false
  let reason: string

  if (bmi !== undefined) {
    if (bmi < BMI_REVIEW_MIN) {
      finalTrack = 'microdose'
      showBmiRedirect = true
      reason = `Based on your measurements (BMI ${bmi.toFixed(1)}), a clinician review is needed first.`
    } else if (bmi < BMI_MICRO_MAX) {
      finalTrack = 'microdose'
      reason = `Based on your measurements (BMI ${bmi.toFixed(1)}), a low-dose clinician-guided approach may fit.`
    } else if (bmi >= BMI_COMORB_MIN && bmi < BMI_CORE_MIN) {
      if (inferredHasComorbidities) {
        finalTrack = 'core'
        reason = `Based on your BMI ${bmi.toFixed(1)} and profile, a therapeutic approach may be appropriate.`
      } else {
        finalTrack = 'microdose'
        reason = `Based on your BMI ${bmi.toFixed(1)}, clinician review will confirm the safest approach.`
      }
    } else {
      finalTrack = 'core'
      reason = `Based on your BMI ${bmi.toFixed(1)}, a therapeutic approach may be appropriate.`
    }
  } else {
    finalTrack = getInitialTrack(weight_goal)
    reason = 'We will personalize recommendations after reviewing your measurements and profile.'
  }

  const isFirstTimer = ['first_time', 'not_sure'].includes(glp1_experience || '')
  const hasDisqualifying = hasDisqualifyingConditions(safety_screening)
  const needsReview =
    (requiresProviderReview(safety_screening) && !hasDisqualifying) ||
    (bmi !== undefined && bmi < BMI_REVIEW_MIN) ||
    (bmi !== undefined && bmi >= BMI_COMORB_MIN && bmi < BMI_CORE_MIN && !inferredHasComorbidities)

  const canProceed = !hasDisqualifying && !(bmi !== undefined && bmi < BMI_REVIEW_MIN)

  return {
    track: finalTrack,
    reason,
    show_bmi_redirect: showBmiRedirect,
    show_first_timer_education: finalTrack === 'microdose' && isFirstTimer,
    can_proceed: canProceed,
    requires_review: needsReview,
    bmi,
  }
}
